import allImgPaths from "assets/images";
import React, { useState } from "react";

const ClaimGraphics = ({ claim, setClaimFiguresTotal = () => {} }: any) => {
  const figures = claim?.figures || [];
  const elements = claim?.elements || [];

  // Helper function to get unique figures count
  const getUniqueFiguresCount = () => {
    const uniqueFigures = new Set<string>();

    // Add claim figures
    figures.forEach((figure: string) => uniqueFigures.add(figure));

    // Add element figures
    elements.forEach((element: any) => {
      // Current structure: element.figures
      if (element.figures && !element.features) {
        element.figures.forEach((figure: string) => uniqueFigures.add(figure));
      }

      // New structure: element.features[].figures
      if (element.features && element.features.length > 0) {
        element.features.forEach((feature: any) => {
          if (feature.figures && feature.figures.length > 0) {
            feature.figures.forEach((figure: string) =>
              uniqueFigures.add(figure)
            );
          }
        });
      }
    });

    return uniqueFigures.size;
  };

  const [loadingStates, setLoadingStates] = useState<boolean[]>(
    Array(getUniqueFiguresCount()).fill(true)
  );

  const transformLegend = (figure: string) => {
    // Search in claim-level legends
    const legendData = claim?.legends.find((legend: any) =>
      legend.hasOwnProperty(figure)
    );

    if (legendData && legendData[figure]) {
      return legendData[figure].map((item: any) => {
        const key = Object.keys(item)[0];
        return {
          number: key,
          text: item[key],
        };
      });
    }

    // Search in element-level legends
    for (const element of elements) {
      const elementLegends = element.legends || [];
      const found = elementLegends.find((legend: any) =>
        legend.hasOwnProperty(figure)
      );
      if (found && found[figure]) {
        return found[figure].map((item: any) => {
          const key = Object.keys(item)[0];
          return {
            number: key,
            text: item[key],
          };
        });
      }
    }

    return [];
  };

  const handleImageLoad = (index: number) => {
    setLoadingStates((prev) => {
      const newLoadingStates = [...prev];
      newLoadingStates[index] = false;
      return newLoadingStates;
    });
  };

  // Function to deduplicate images and legends
  const getUniqueFiguresAndLegends = () => {
    const uniqueFigures = new Set();

    const uniqueEntries: any[] = [];

    figures.forEach((figure: any) => {
      const legendItems = transformLegend(figure);
      // figurePreSignedUrls is now an object with figure names as keys
      const imageUrl = claim?.figurePreSignedUrls?.[figure];

      if (!uniqueFigures.has(figure)) {
        uniqueFigures.add(figure);
        uniqueEntries.push({ figure, legendItems, imageUrl });
      }
    });

    elements.forEach((element: any) => {
      // Handle current structure: element.figures
      if (element.figures && !element.features) {
        const elementFigures = element.figures || [];
        elementFigures.forEach((figure: any) => {
          const legendItems = transformLegend(figure);
          // figurePreSignedUrls is now an object with figure names as keys
          const imageUrl = element.figurePreSignedUrls?.[figure];

          if (!uniqueFigures.has(figure)) {
            uniqueFigures.add(figure);
            uniqueEntries.push({ figure, legendItems, imageUrl });
          }
        });
      }

      // Handle new structure: element.features[].figures
      if (element.features && element.features.length > 0) {
        element.features.forEach((feature: any) => {
          const featureFigures = feature.figures || [];
          featureFigures.forEach((figure: any) => {
            const legendItems = transformLegend(figure);
            // figurePreSignedUrls is now an object with figure names as keys
            const imageUrl = feature.figurePreSignedUrls?.[figure];

            if (!uniqueFigures.has(figure)) {
              uniqueFigures.add(figure);
              uniqueEntries.push({ figure, legendItems, imageUrl });
            }
          });
        });
      }
    });

    return uniqueEntries;
  };

  if (figures.length === 0 && elements.length === 0) {
    return null;
  }

  // Get unique figures and their corresponding legends
  const uniqueEntries = getUniqueFiguresAndLegends();

  setClaimFiguresTotal(uniqueEntries.length);

  return (
    <div className="mb-5 space-y-8">
      {/* Loop through unique entries to display their images and legends */}
      {uniqueEntries.map(({ figure, legendItems, imageUrl }, entryIndex) => (
        <div key={figure} className="w-full">
          <div className="flex gap-x-8">
            {/* Left side - Image */}
            <div className="w-1/2">
              <div className="flex justify-center">
                {loadingStates[entryIndex] && (
                  <div className="w-[348px] border rounded-lg p-4 animate-pulse">
                    <div className="space-y-4">
                      <div className="h-56 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                )}
                <img
                  src={imageUrl}
                  onLoad={() => handleImageLoad(entryIndex)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = allImgPaths.claimFigureNotFound; // Fallback image
                  }}
                  alt={`Patent figure ${figure}`}
                  className={`w-full max-w-sm object-contain ${
                    loadingStates[entryIndex] ? "hidden" : "block"
                  }`}
                />
              </div>
            </div>

            {/* Right side - Legend */}
            <div className="w-1/2">
              <ul className="space-y-2">
                {legendItems.map((item: any) => (
                  <li
                    key={item.number}
                    className="text-sm text-gray-900 font-interRegular"
                  >
                    {item.number}. {item.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Figure caption below */}
          <div className="mt-4 bg-[#F9FAFB] py-2 px-3 text-center w-11/12 mx-auto">
            <span className="text-sm text-[#272E35] font-interMedium">
              Fig. {figure.replace("FIG_", "")}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ClaimGraphics;
